'use client'

/**
 * /login — unified sign-in form (client island).
 *
 * Drives the Better Auth endpoints under /api/auth/*:
 *   - Credentials sign-in (email + password) -> /api/auth/sign-in/email
 *   - Social sign-in (Google / Facebook)     -> /api/auth/sign-in/social
 *
 * Supports OAuth2 / OIDC redirects via return_to, returnTo, or callbackURL query
 * parameter, defaulting to /account.
 *
 * THE RETURN_TO VALUE IS ATTACKER-CONTROLLED. It comes off the query string and
 * ends up on `window.location.href`, so it is the same open-redirect surface
 * that /logout and /openid_provider guard — and it must use the SAME policy, not
 * a second opinion about what "safe" means. Hence sanitizeReturnTo from
 * lib/authServer/returnTo.js: one allowlist, three sinks. That module is pure
 * ESM with no imports at all (no node builtins, no config), so the client bundle
 * takes it as-is.
 */

import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { sanitizeReturnTo, DEFAULT_RETURN_TO_ORIGINS, buildTermsRedirect } from '../../lib/authServer/returnTo'
import styles from '../../styles/auth.module.css'

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.66-5.17 3.66-9.17z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.34 24 12 24z"
      />
      <path
        fill="#FBBC05"
        d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.18 0 9.99 0 12s.45 3.82 1.25 5.42l4.03-3.15z"
      />
      <path
        fill="#EA4335"
        d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.34 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z"
      />
    </svg>
  )
}

function FacebookIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="#1877F2" aria-hidden="true">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  )
}

export default function LoginForm({ config = {} }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [returnTo, setReturnTo] = useState('/account')
  const [submitting, setSubmitting] = useState(false)
  const [socialLoading, setSocialLoading] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const requested =
      params.get('return_to') || params.get('returnTo') || params.get('callbackURL') || '/account'
    // Sanitise HERE, at the one point the untrusted value enters — so the
    // `returnTo` state can only ever hold a vetted destination and every later
    // use of it (the redirect below, the post-sign-in redirect, and the
    // callbackURL we POST to the sign-in endpoint) is safe by construction
    // rather than by each site remembering to check. Rejected values become
    // /account, so the feature keeps working for legitimate targets.
    const target = sanitizeReturnTo(requested, DEFAULT_RETURN_TO_ORIGINS)
    setReturnTo(target)

    // Skip the form entirely when the member is already past it. ONE question to
    // ONE endpoint: /terms-status reports both "is there a session" and "is
    // there a half-finished terms gate", which are now genuinely different
    // states — a terms-pending member has NO session, so asking get-session
    // could not distinguish them. A failure here is non-fatal: we simply render
    // the form, which is the correct fallback for "we could not tell".
    fetch('/api/auth/terms-status', { credentials: 'same-origin' })
      .then((res) => (res.ok ? res.json() : null))
      .then((status) => {
        if (!status) return
        if (status.termsPending) {
          // /accept-terms reads this back out and redirects to it in turn, so an
          // unsanitised value here would just move the open redirect one hop.
          // buildTermsRedirect is the shared builder and owns the encoding.
          window.location.href = buildTermsRedirect(target)
          return
        }
        if (status.authenticated) window.location.href = target
      })
      .catch(() => {})
  }, [])

  async function onSubmit(e) {
    e.preventDefault()
    if (submitting || socialLoading) return
    setSubmitting(true)
    setError('')
    try {
      const res = await fetch('/api/auth/sign-in/email', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, password, callbackURL: returnTo }),
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok) {
        window.location.href = returnTo
        return
      }
      // The terms gate answers 403 with a redirect it built itself. Trust that
      // value over a locally-assembled one: the server sanitised returnTo
      // against the redirect allowlist and it is the only side that knows the
      // OAuth leg's original destination.
      if (res.status === 403 && data?.code === 'TERMS_NOT_ACCEPTED' && data?.redirect) {
        window.location.href = data.redirect
        return
      }
      setError(data?.message || data?.error || 'Invalid email or password. Please try again.')
    } catch (err) {
      console.error('[LoginForm] sign-in error:', err)
      setError('We could not reach the server. Please check your connection and try again.')
    } finally {
      setSubmitting(false)
    }
  }

  async function onSocialLogin(provider) {
    if (submitting || socialLoading) return
    setSocialLoading(provider)
    setError('')
    try {
      const res = await fetch('/api/auth/sign-in/social', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ provider, callbackURL: returnTo }),
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data?.url) {
        window.location.href = data.url
        return
      }
      setError((data && (data.message || data.error)) || `Could not sign in with ${provider}. Please try again.`)
    } catch {
      setError('We could not reach the server. Please check your connection and try again.')
    } finally {
      setSocialLoading(null)
    }
  }

  const isBusy = submitting || !!socialLoading

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.eyebrow}>Account access</div>
        <h1 className={styles.h1}>Sign in</h1>
        <p className={styles.lede}>Access your Virtual Racing School account.</p>

        <form onSubmit={onSubmit} noValidate={false}>
          <div className={styles.field}>
            <label htmlFor="email">Email address</label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isBusy}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="password">Password</label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isBusy}
            />
          </div>

          <div className={styles.forgotLink}>
            <a href="/forgot-password">Forgot your password?</a>
          </div>

          <button type="submit" className={styles.submit} disabled={isBusy}>
            {submitting ? 'Signing in…' : 'Log in'}
          </button>

          {error ? <p className={styles.errorMsg}>{error}</p> : null}
        </form>

        <div className={styles.divider}>or</div>

        <div className={styles.socialButtons}>
          <button
            type="button"
            className={styles.socialButton}
            onClick={() => onSocialLogin('google')}
            disabled={isBusy}
          >
            <GoogleIcon />
            <span>{socialLoading === 'google' ? 'Redirecting…' : 'Login with Google'}</span>
          </button>
          <button
            type="button"
            className={styles.socialButton}
            onClick={() => onSocialLogin('facebook')}
            disabled={isBusy}
          >
            <FacebookIcon />
            <span>{socialLoading === 'facebook' ? 'Redirecting…' : 'Login with Facebook'}</span>
          </button>
        </div>

        <p className={styles.finePrint}>
          Don&apos;t have an account? <a href="/signup">Sign up</a>
        </p>
      </div>
    </div>
  )
}

LoginForm.propTypes = {
  config: PropTypes.object,
}
